import React, { forwardRef } from 'react';
import StylovyzeDialog from '../StylovyzeDialog/StylovyzeDialog';
import { WarningDialogProps } from './WarningDialog.types';

/**
 * WarningDialog - A semantic wrapper around StylovyzeDialog with warning-specific styling
 *
 * Provides a consistent interface for warning dialogs across the application.
 * Currently styled identically to StylovyzeDialog with error-colored confirm button.
 * Designed for future extensibility if warning-specific visual changes are needed.
 *
 * @see {@link https://stylovyze.info360-dev.com/?path=/story/ds-notifications-modals-warningdialog--primary} Storybook Documentation
 * @see {@link https://stylovyze.info360-dev.com/?path=/story/ds-notifications-modals-stylovyzecard--primary} StylovyzeDialog Documentation
 *
 * @example
 * // Standard usage
 * <WarningDialog
 *   open={isOpen}
 *   title="Confirm Action"
 *   content="This action cannot be undone. Are you sure?"
 *   cancelText="Cancel"
 *   confirmText="Delete"
 *   onCancel={() => setIsOpen(false)}
 *   onConfirm={handleDelete}
 * />
 *
 * @example
 * // With JSX content
 * <WarningDialog
 *   open={isOpen}
 *   title="System Warning"
 *   content={
 *     <div>
 *       <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
 *         <WarningIcon color="error" style={{ marginRight: '8px' }} />
 *         <strong>Critical System Alert</strong>
 *       </div>
 *       <p>This operation will affect multiple systems and cannot be undone.</p>
 *       <ul>
 *         <li>Database records will be permanently deleted</li>
 *         <li>User sessions will be terminated</li>
 *         <li>Backup systems will be notified</li>
 *       </ul>
 *     </div>
 *   }
 *   cancelText="Cancel"
 *   confirmText="Proceed"
 *   onCancel={() => setIsOpen(false)}
 *   onConfirm={handleProceed}
 * />
 */
const WarningDialog = forwardRef<HTMLDivElement, WarningDialogProps>(
	(props, ref) => {
		return (
			<StylovyzeDialog
				{...props}
				// ref is not a valid prop for StylovyzeDialog, so do not pass it down
				confirmButtonProps={{
					...props.confirmButtonProps,
					color: 'error',
				}}
			/>
		);
	},
);

WarningDialog.displayName = 'WarningDialog';

export default WarningDialog;
